import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Button, Box, CircularProgress, 
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, IconButton, Paper
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloseIcon from '@mui/icons-material/Close';
import { QRCodeSVG } from 'qrcode.react';
import API_URL from '../config/api';

const Input = styled('input')({
  display: 'none',
});

const RestaurantForm = ({ open, onClose, restaurant, onSave }) => {
  const [formData, setFormData] = useState({
    restaurantId: '',
    restaurantName: '',
    description: '',
    menuImages: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (restaurant) {
      setFormData({
        restaurantId: restaurant.restaurant_id,
        restaurantName: restaurant.restaurant_name,
        description: restaurant.description || '',
        menuImages: []
      });
    } else {
      setFormData({
        restaurantId: `rest_${Math.random().toString(36).substr(2, 9)}`,
        restaurantName: '',
        description: '',
        menuImages: []
      });
    }
  }, [restaurant]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      menuImages: [...prev.menuImages, ...files]
    }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      menuImages: prev.menuImages.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('restaurantId', formData.restaurantId);
      formDataToSend.append('restaurantName', formData.restaurantName);
      formDataToSend.append('description', formData.description);

      formData.menuImages.forEach(image => {
        formDataToSend.append('menuImages', image);
      });

      const response = await fetch(`${API_URL}/api/admin/restaurants`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Failed to save restaurant');
      }

      const savedRestaurant = await response.json();
      onSave(savedRestaurant);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {restaurant ? 'Edit Restaurant' : 'Add New Restaurant'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" noValidate sx={{ mt: 2 }}>
          <TextField
            name="restaurantId"
            label="Restaurant ID"
            value={formData.restaurantId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            disabled
          />
          
          <TextField
            name="restaurantName"
            label="Restaurant Name"
            value={formData.restaurantName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          
          <TextField
            name="description"
            label="Description"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Menu Images
            </Typography>
            
            <label htmlFor="menu-images">
              <Input
                id="menu-images"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
              <Button
                variant="outlined"
                component="span"
                startIcon={<AddIcon />}
              >
                Add Menu Images
              </Button>
            </label>
            
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.menuImages.map((image, index) => (
                <Paper
                  key={index}
                  sx={{ 
                    position: 'relative',
                    width: 100,
                    height: 100
                  }}
                >
                  <IconButton
                    size="small"
                    sx={{ 
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      bgcolor: 'rgba(255,255,255,0.8)'
                    }}
                    onClick={() => handleRemoveImage(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Menu ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Paper>
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AdminDashboard = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', severity: 'success' });
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/restaurants`);
      if (!response.ok) {
        throw new Error('Failed to fetch restaurants');
      }
      const data = await response.json();
      setRestaurants(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const handleAddRestaurant = () => {
    setEditingRestaurant(null);
    setShowForm(true);
  };

  const handleEditRestaurant = (restaurant) => {
    setEditingRestaurant(restaurant);
    setShowForm(true);
  };

  const handleDeleteRestaurant = async (restaurantId) => {
    if (!window.confirm('Are you sure you want to delete this restaurant?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete restaurant');
      }

      setNotification({
        show: true,
        message: 'Restaurant deleted successfully',
        severity: 'success'
      });
      fetchRestaurants();
    } catch (err) {
      setNotification({
        show: true,
        message: err.message,
        severity: 'error'
      });
    }
  };

  const handleSave = async (savedRestaurant) => {
    setNotification({
      show: true,
      message: `Restaurant ${editingRestaurant ? 'updated' : 'created'} successfully`,
      severity: 'success'
    });
    fetchRestaurants();
  };

  const handleQrPreview = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setQrModalOpen(true);
  };

  const downloadQrCode = () => {
    if (!selectedRestaurant) return;
    
    const svg = document.getElementById('preview-qr-code');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = pngFile;
      downloadLink.download = `${selectedRestaurant.restaurant_id}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Restaurant Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddRestaurant}
        >
          Add Restaurant
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
          {restaurants.map(restaurant => (
            <Paper
              key={restaurant.restaurant_id}
              sx={{ p: 3 }}
              elevation={2}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  {restaurant.restaurant_name}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditRestaurant(restaurant)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteRestaurant(restaurant.restaurant_id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              {restaurant.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {restaurant.description}
                </Typography>
              )}

              <Typography variant="subtitle2" gutterBottom>
                Menu Images: {restaurant.menu_images?.length || 0}
              </Typography>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<QrCodeIcon />}
                  onClick={() => handleQrPreview(restaurant)}
                  size="medium"
                >
                  View QR Code
                </Button>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <RestaurantForm
        open={showForm}
        onClose={() => setShowForm(false)}
        restaurant={editingRestaurant}
        onSave={handleSave}
      />

      <Dialog 
        open={qrModalOpen} 
        onClose={() => setQrModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">QR Code - {selectedRestaurant?.restaurant_name}</Typography>
            <IconButton onClick={() => setQrModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            {selectedRestaurant && (
              <>
                <QRCodeSVG
                  id="preview-qr-code"
                  value={`${window.location.origin}/menu/${selectedRestaurant.restaurant_id}`}
                  size={300}
                  level="H"
                  includeMargin
                />
                <Button
                  variant="contained"
                  startIcon={<CloudDownloadIcon />}
                  onClick={downloadQrCode}
                  sx={{ mt: 3 }}
                >
                  Download QR Code
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={notification.show}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, show: false }))}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;